import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ActivitiesCard.module.scss';

interface Activity {
  id: number;
  heading: string;
  image: string;
  description: string;
  date: string;
}

interface ActivitiesListProps {
  activities: Activity[];
  className?: string;
  onCardClick?: (activity: Activity) => void;
}

const ActivitiesList: React.FC<ActivitiesListProps> = ({ activities, className = '', onCardClick }) => {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCardClick = (activity: Activity) => {
    if (onCardClick) {
      onCardClick(activity);
    }
  };

  return (
    <div className={`${styles.activitiesContainer} ${className}`}>
      {activities.map((activity) => (
        <Link to={`/activities/${activity.id}`} key={activity.id} style={{ textDecoration: 'none' }}>
          <article className={styles.card} onClick={() => handleCardClick(activity)} role='button' tabIndex={0}>
            <div className={styles.imageContainer}>
              <img src={activity.image} alt={activity.heading} className={styles.image} loading='lazy' />
              <div className={styles.imageOverlay} />
            </div>
            <div className={styles.content}>
              <div className={styles.dateBadge}>{formatDate(activity.date)}</div>
              <h3 className={styles.heading}>
                {t(`activitiesData.${activity.id}.heading`, { defaultValue: activity.heading })}
              </h3>
              <p className={styles.description}>
                {t(`activitiesData.${activity.id}.description`, {
                  defaultValue: activity.description,
                })}
              </p>
              <div className={styles.footer}>
                <span className={styles.readMore}>
                  {t('activitiesData.readMore', { defaultValue: 'Read more' })}
                  <svg className={styles.arrow} viewBox='0 0 24 24' width='16' height='16'>
                    <path fill='currentColor' d='M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z' />
                  </svg>
                </span>
              </div>
            </div>
          </article>
        </Link>
      ))}
    </div>
  );
};

export default ActivitiesList;
